import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Calendar, CheckCircle2, XCircle, Bell } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useOwnerNotifications } from '../hooks/useOwnerNotifications';
import { sendApprovalRequest } from '../services/sms';

const servicePackages = [
  { value: 'basic', label: 'Basic Wash & Vacuum' },
  { value: 'full', label: 'Full Interior & Exterior Detail' },
  { value: 'premium', label: 'Premium / Full Restoration' },
];

const vehicleSizes = ['Small', 'Medium', 'Large', 'XL / Oversized'];
const conditions = ['Light', 'Moderate', 'Heavy / Severe'];

const initialForm = {
  name: '',
  email: '',
  phone: '',
  vehicleSize: vehicleSizes[0],
  condition: conditions[0],
  service: servicePackages[0].value,
  preferredDate: '',
  notes: '',
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const serviceFromQuery = searchParams.get('service');
  const sizeFromQuery = searchParams.get('size');

  const formDefaults = useMemo(() => {
    const matchedService =
      servicePackages.find((pkg) => pkg.value === serviceFromQuery)?.value ?? initialForm.service;
    const matchedSize = vehicleSizes.includes(sizeFromQuery ?? '') ? sizeFromQuery : initialForm.vehicleSize;
    return {
      ...initialForm,
      service: matchedService,
      vehicleSize: matchedSize ?? initialForm.vehicleSize,
    };
  }, [serviceFromQuery, sizeFromQuery]);

  const [form, setForm] = useState(formDefaults);
  const [requests, setRequests] = useState([]);
  const [message, setMessage] = useState('');
  const [notificationState, setNotificationState] = useState(null);
  const [smsState, setSmsState] = useState(null);
  const { permission, requestPermission, sendNotification, supported } = useOwnerNotifications();

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      service: formDefaults.service,
      vehicleSize: formDefaults.vehicleSize,
    }));
    setMessage('');
  }, [formDefaults.service, formDefaults.vehicleSize]);

  const permissionCopy = useMemo(() => {
    if (!supported) {
      return 'Notifications are not supported on this device.';
    }
    if (permission === 'granted') {
      return 'Push notifications are enabled for owner approvals.';
    }
    if (permission === 'denied') {
      return 'Notifications were blocked. Enable them in your browser settings to receive instant approvals.';
    }
    return 'Enable browser notifications so the owner/cleaner sees approvals instantly.';
  }, [permission, supported]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm(() => ({ ...formDefaults }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.preferredDate) {
      setMessage('Please complete the required fields before sending your request.');
      return;
    }

    const newRequest = {
      id: Date.now().toString(),
      ...form,
      status: 'pending',
      submittedAt: new Date(),
    };

    setRequests((prev) => [newRequest, ...prev]);
    setMessage('Thanks! Your detailing request has been logged. We will confirm shortly.');
    setNotificationState(null);
    setSmsState(null);

    const notified = await sendNotification({
      title: 'New detailing request waiting for approval',
      body: `${form.name} requested the ${servicePackages.find((pkg) => pkg.value === form.service)?.label ?? 'selected package'} for a ${form.vehicleSize} vehicle.`,
      tag: newRequest.id,
    });

    setNotificationState(notified ? 'sent' : notified === false ? 'blocked' : notified);

    const smsResult = await sendApprovalRequest({
      name: form.name,
      phone: form.phone,
      vehicleSize: form.vehicleSize,
      condition: form.condition,
      serviceLabel:
        servicePackages.find((pkg) => pkg.value === form.service)?.label ?? 'Selected package',
      preferredDate: form.preferredDate,
    });
    setSmsState(smsResult);

    resetForm();
    setTimeout(() => {
      document.getElementById('approval')?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  const handleApproval = (id, nextStatus, payload = {}) => {
    setRequests((prev) =>
      prev.map((request) =>
        request.id === id
          ? {
              ...request,
              status: nextStatus,
              ...payload,
              resolvedAt: new Date(),
            }
          : request,
      ),
    );
  };

  const handleReschedule = (id) => {
    const newDate = window.prompt('Enter new date/time (e.g. 2024-07-15 2:00 PM):');
    if (!newDate) return;
    handleApproval(id, 'reschedule', { rescheduledFor: newDate });
  };

  return (
    <section className="relative overflow-hidden py-20">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#21080c] via-[#160508] to-[#21080c]" />
      <div className="mx-auto flex max-w-6xl flex-col gap-16 px-4">
        <motion.div
          className="mx-auto max-w-3xl text-center"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-white/70">
            Book A Mobile Detail
          </span>
          <h1 className="mt-4 text-3xl font-bold text-white sm:text-4xl">
            Send your request—owner approval happens instantly.
          </h1>
          <p className="mt-4 text-sm text-white/70 sm:text-base">
            Tell us about your vehicle and desired package. We’ll respond with a confirmation and
            notify the lead cleaner for approval in real time.
          </p>
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-[3fr_2fr]">
          <motion.form
            onSubmit={handleSubmit}
            className="space-y-6 rounded-3xl border border-white/10 bg-[#1c0d11]/80 p-6 shadow-xl shadow-black/40"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
                  Name *
                </label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-brand-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-brand-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
                  Phone *
                </label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="(647) 000-0000"
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-brand-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
                  Preferred date *
                </label>
                <div className="mt-2 flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                  <Calendar className="h-4 w-4 text-brand-200" />
                  <input
                    type="date"
                    name="preferredDate"
                    value={form.preferredDate}
                    onChange={handleChange}
                    className="w-full bg-transparent text-sm text-white focus:outline-none"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
                  Vehicle Size
                </label>
                <select
                  name="vehicleSize"
                  value={form.vehicleSize}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-brand-400 focus:outline-none"
                >
                  {vehicleSizes.map((size) => (
                    <option key={size} value={size} className="bg-slate-900">
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
                  Condition Level
                </label>
                <select
                  name="condition"
                  value={form.condition}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-brand-400 focus:outline-none"
                >
                  {conditions.map((condition) => (
                    <option key={condition} value={condition} className="bg-slate-900">
                      {condition}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
                  Service Package
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-brand-400 focus:outline-none"
                >
                  {servicePackages.map((pkg) => (
                    <option key={pkg.value} value={pkg.value} className="bg-slate-900">
                      {pkg.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
                Notes for the team
              </label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Tell us about access to power, pet hair, odor concerns, or deadlines."
                rows={4}
                className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-brand-400 focus:outline-none"
              />
            </div>

            <div className="space-y-3 rounded-2xl border border-white/10 bg-[#1c0d11]/80 p-4 text-sm text-white/70">
              <div className="flex items-center gap-3">
                <Bell className="h-5 w-5 text-brand-200" />
                <p className="font-semibold text-white">Owner approval notifications</p>
              </div>
              <p>{permissionCopy}</p>
              {permission !== 'granted' && supported && (
                <button
                  type="button"
                  onClick={requestPermission}
                  className="rounded-full border border-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-white transition hover:border-white/40"
                >
                  Enable Notifications
                </button>
              )}
              {notificationState === 'sent' && (
                <p className="flex items-center gap-2 text-sm text-brand-200">
                  <CheckCircle2 className="h-5 w-5" />
                  Approval alert sent to the owner/cleaner.
                </p>
              )}
              {notificationState === 'blocked' && (
                <p className="flex items-center gap-2 text-sm text-orange-300">
                  <XCircle className="h-5 w-5" />
                  We could not send an alert—check notification permissions.
                </p>
              )}
              {smsState === 'sent' && (
                <p className="flex items-center gap-2 text-sm text-brand-200">
                  <CheckCircle2 className="h-5 w-5" />
                  SMS approval request sent successfully.
                </p>
              )}
              {smsState === 'skipped' && (
                <p className="text-sm text-white/60">
                  SMS approvals are not configured yet. Set <code className="font-mono text-xs">VITE_SMS_WEBHOOK_URL</code> to enable them.
                </p>
              )}
              {smsState === 'error' && (
                <p className="flex items-center gap-2 text-sm text-orange-300">
                  <XCircle className="h-5 w-5" />
                  SMS approval failed—check your webhook logs.
                </p>
              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              className="w-full rounded-full bg-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-500/30 transition hover:bg-brand-400"
            >
              Submit For Approval
            </motion.button>

            {message && (
              <p className="text-sm text-white/70">
                {message}
              </p>
            )}
          </motion.form>

          <motion.aside
            className="space-y-8"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            <div className="rounded-3xl border border-white/10 bg-[#1c0d11]/80 p-6 text-sm text-white/70">
              <h2 className="text-lg font-semibold text-white">Contact information</h2>
              <p className="mt-2 text-white/60">
                Want to skip the form? Call or text with photos for a rapid quote.
              </p>
              <div className="mt-4 space-y-3">
                <a
                  href="tel:+16475550147"
                  className="flex items-center gap-3 rounded-2xl border border-white/10 px-4 py-3 transition hover:border-white/30 hover:text-white"
                >
                  <Phone className="h-5 w-5 text-brand-200" />
                  (647) 555-0147
                </a>
                <a
                  href="mailto:hello@shazam-detailing.ca"
                  className="flex items-center gap-3 rounded-2xl border border-white/10 px-4 py-3 transition hover:border-white/30 hover:text-white"
                >
                  <Mail className="h-5 w-5 text-brand-200" />
                  hello@shazam-detailing.ca
                </a>
                <div className="flex items-center gap-3 rounded-2xl border border-white/10 px-4 py-3">
                  <MapPin className="h-5 w-5 text-brand-200" />
                  Greater Toronto Area · Mobile only
                </div>
              </div>
              <div className="mt-6 space-y-2 text-xs uppercase tracking-[0.25em] text-white/40">
                <p>Business hours</p>
                <div className="space-y-1 text-white/70">
                  <p>Mon–Fri · 8:00AM – 6:00PM</p>
                  <p>Saturday · 9:00AM – 5:00PM</p>
                  <p>Sunday · Closed (emergency slots by request)</p>
                </div>
              </div>
            </div>

            <div
              id="approval"
              className="space-y-4 rounded-3xl border border-white/10 bg-[#1c0d11]/80 p-6 text-sm text-white/70"
            >
              <h2 className="text-lg font-semibold text-white">Approval dashboard</h2>
              <p>
                Track every incoming request. Approve or decline right here after you review
                photos or vehicle notes.
              </p>
              <div className="space-y-4">
                {requests.length === 0 && (
                  <p className="rounded-2xl border border-dashed border-white/10 px-4 py-6 text-center text-white/50">
                    No pending approvals yet. Incoming requests will land here instantly.
                  </p>
                )}
                {requests.map((request) => (
                  <div
                    key={request.id}
                    className="space-y-3 rounded-2xl border border-white/10 bg-black/30 p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-white">{request.name}</p>
                        <p className="text-xs text-white/50">
                          {servicePackages.find((pkg) => pkg.value === request.service)?.label} ·{' '}
                          {request.vehicleSize} · {request.condition}
                        </p>
                      </div>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] ${
                          request.status === 'approved'
                            ? 'bg-green-500/20 text-green-200'
                            : request.status === 'declined'
                            ? 'bg-red-500/20 text-red-200'
                            : 'bg-white/10 text-white/70'
                        }`}
                      >
                        {request.status}
                      </span>
                    </div>
                    <p className="text-xs text-white/60">
                      Preferred date: {new Date(request.preferredDate).toLocaleDateString('en-CA')} · Submitted {request.submittedAt.toLocaleString()}
                    </p>
                    {request.rescheduledFor && (
                      <p className="text-xs text-brand-200">
                        Rescheduled for: {request.rescheduledFor}
                      </p>
                    )}
                    {request.notes && (
                      <p className="text-sm text-white/70">Notes: {request.notes}</p>
                    )}
                    {request.status === 'pending' && (
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <button
                          type="button"
                          onClick={() => handleApproval(request.id, 'approved')}
                          className="flex-1 rounded-full bg-green-500/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-green-200 transition hover:bg-green-500/30"
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => handleApproval(request.id, 'declined')}
                          className="flex-1 rounded-full bg-red-500/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-red-200 transition hover:bg-red-500/30"
                        >
                          Decline
                        </button>
                        <button
                          type="button"
                          onClick={() => handleReschedule(request.id)}
                          className="flex-1 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-white/20"
                        >
                          Reschedule
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}
